import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { DateSelectArg, EventClickArg, EventInput } from "@fullcalendar/common";
import { DateClickArg } from "@fullcalendar/interaction";
import scrollGridPlugin from "@fullcalendar/scrollgrid";
import Dropdown from "antd/es/dropdown";
import { roundToNearestMinutes } from "date-fns/esm";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivitySuggestion from "@mapmycustomers/shared/types/entity/ActivitySuggestion";
import User from "@mapmycustomers/shared/types/User";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import BaseCalendar, { fullCalendarViewNameToViewMode } from "@app/component/BaseCalendar";
import { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import CalendarViewComponent from "@app/component/view/CalendarView/types/CalendarViewComponent";
import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { deleteActivity, openDuplicationModal } from "@app/scene/activity/store/actions";
import { getDeleteLoadingIds } from "@app/scene/activity/store/selectors";
import useEventDropResize from "@app/scene/activity/utils/useEventDropResize";
import useEventRenderer from "@app/scene/activity/utils/useEventRenderer";
import { getCurrentUserId, isCurrentUserReadOnly } from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import {
  getUserColorClassNameGetter,
  UserColorClassNameGetter,
} from "@app/store/userColor/selectors";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import ActivityMenu from "../../ActivityMenu";
import { moveResizeActivity, updateActivityInList } from "../store/actions";
import EventObject from "../type/EventObject";
import ICalendarContext from "../type/ICalendarContext";
import CalendarContext from "../utils/CalendarContext";
import useEventClassNamesGetter from "../utils/useEventClassNamesGetter";

import ActivityCadenceModal from "./ActivityCadenceModal";
import ActivityCalendarEvent from "./ActivityCalendarEvent";
import styles from "./Calendar.module.scss";
import NearbyModal from "./NearbyModal";

interface Props {
  currentUserId: User["id"];
  deleteActivity: typeof deleteActivity.request;
  deleteLoadingIds: Activity["id"][];
  events: EventInput[];
  getUserColorClassName: UserColorClassNameGetter;
  isReadOnlyMember: boolean;
  onCreateActivity: typeof showGlobalCreateActivityModal;
  onMoveActivity: typeof moveResizeActivity.request;
  openDuplicationModal: typeof openDuplicationModal.request;
  updateActivityInList: typeof updateActivityInList;
  users: User[];
}

const Calendar: React.FC<Props> = ({
  currentUserId,
  deleteActivity,
  deleteLoadingIds,
  events,
  getUserColorClassName,
  isReadOnlyMember,
  onCreateActivity,
  onMoveActivity,
  openDuplicationModal,
  updateActivityInList,
  users,
}) => {
  const { register, unregister, updateViewStateAndFetch, viewState } = useCalendarView<Activity>();
  const [annotationElement, setAnnotationElement] = useState<HTMLElement | undefined>();
  const [selectedActivityId, selectActivityId] = useState<Activity["id"] | undefined>();
  const [activityForContextMenu, setActivityForContextMenu] = useState<Activity | undefined>();
  const [contextMenuCoordinates, setContextMenuCoordinates] = useState<[number, number]>([0, 0]);
  const [calendarCoordinates, setCalendarCoordinates] = useState<[number, number]>([0, 0]);
  const [calendarElement, setCalendarElement] = useState<HTMLElement | undefined>();

  useEffect(() => {
    const listener: CalendarViewComponent<Activity> = {
      onViewStateChange: updateViewStateAndFetch,
    };
    register(listener);
    return () => {
      unregister(listener);
    };
  }, [register, unregister, updateViewStateAndFetch]);

  const handleClick = useCallback(
    (e: EventClickArg) => {
      const payload: EventObject = e.event.extendedProps as EventObject;
      // do not set clicked event if it has no Activity information in it
      if (payload.activity) {
        const activity: Activity | undefined = events.find((event) => event.id === e.event?.id)
          ?.extendedProps?.activity;

        if (activity) {
          setAnnotationElement(e.el);
          selectActivityId(activity.id);
        }
        smartPlannerViewAnalytics.clicked(["Individual Activity on Calendar"]);
        return;
      }
      if (payload.suggestion) {
        const prefilledValues: Partial<Omit<Activity, "id">> = {};
        switch (payload.suggestion.property) {
          case "gap": {
            prefilledValues.startAt = payload.suggestion.startAt;
            break;
          }
          default:
            return; // do nothing for other suggestion types
        }
        onCreateActivity({ activity: prefilledValues });
      }
    },
    [events, onCreateActivity, selectActivityId, setAnnotationElement]
  );

  const eventClassNamesGetter = useEventClassNamesGetter(getUserColorClassName);
  const handleRenderEvent = useEventRenderer(
    calendarCoordinates,
    "activity-calendar-view",
    setActivityForContextMenu,
    setContextMenuCoordinates
  );
  const handleEventDropResize = useEventDropResize(
    currentUserId,
    onMoveActivity,
    users,
    isReadOnlyMember
  );

  const handleDateClick = useCallback(
    (e: DateClickArg) => {
      const viewMode = fullCalendarViewNameToViewMode[e.view.type];
      if (viewMode !== CalendarViewMode.MONTH) {
        onCreateActivity({ activity: { startAt: e.dateStr } });
      } else {
        const startAt = roundToNearestMinutes(
          parseApiDate(`${e.dateStr}T${formatDate(Date.now(), "HH:mm:ss")}`),
          { nearestTo: 15 }
        ).toISOString();
        onCreateActivity({ activity: { startAt } });
        smartPlannerViewAnalytics.clicked(["Clicked Calendar to Create Activity"]);
      }
    },
    [onCreateActivity]
  );

  const handleSelect = useCallback(
    (e: DateSelectArg) => {
      onCreateActivity({
        activity: {
          allDay: e.allDay,
          endAt: e.end.toISOString(),
          startAt: e.start.toISOString(),
        },
      });
      smartPlannerViewAnalytics.clicked(["Clicked and Dragged Calendar to Create Activity"]);
    },
    [onCreateActivity]
  );

  const [nearbyEvent, setNearbyEvent] = useState<EventObject | undefined>();
  const [cadenceSuggestion, setCadenceSuggestion] = useState<ActivitySuggestion | undefined>();
  const contextValue: ICalendarContext = useMemo(
    () => ({
      onCadenceClick: setCadenceSuggestion,
      onNearByClick: setNearbyEvent,
    }),
    [setNearbyEvent]
  );

  const handleChangeActivity = useCallback(
    (activity?: Activity) => activity && updateActivityInList(activity),
    [updateActivityInList]
  );

  const handleCalendarContainerRef = useCallback(
    (eventElement) => {
      if (eventElement) {
        const rect = eventElement.getBoundingClientRect();
        setCalendarCoordinates([rect.x, rect.y]);
      }
    },
    [setCalendarCoordinates]
  );

  const handleDeleteActivity = useCallback(
    (payload: { deleteType: RecurringActionType | undefined; id: Activity["id"] }) => {
      setActivityForContextMenu(undefined);
      deleteActivity(payload);
    },
    [deleteActivity, setActivityForContextMenu]
  );

  const handleOpenDuplicationModal = useCallback(
    (id: Activity["id"]) => {
      setActivityForContextMenu(undefined);
      openDuplicationModal(id);
    },
    [openDuplicationModal, setActivityForContextMenu]
  );

  const scrollTo8 = useDebouncedCallback([
    () => {
      setTimeout(() => {
        if (calendarElement) {
          const hour8AMElement = document.querySelector('[data-time="08:00:00"]') as HTMLElement;
          if (hour8AMElement) {
            const calendarTop = calendarElement.getBoundingClientRect().top;
            const hour8AMElementTop = hour8AMElement.getBoundingClientRect().top;
            calendarElement.scrollTo({
              behavior: "smooth",
              top: hour8AMElementTop - calendarTop - 20,
            });
          }
        }
      }, 500);
    },
    300,
  ]);

  useChangeTracking(() => {
    scrollTo8();
  }, [scrollTo8, viewState.viewMode]);

  return (
    <CalendarContext.Provider value={contextValue}>
      <div className={styles.container} key={viewState.viewMode} ref={handleCalendarContainerRef}>
        <Dropdown
          align={{ offset: contextMenuCoordinates }}
          onOpenChange={() => setActivityForContextMenu(undefined)}
          open={!!activityForContextMenu}
          overlay={
            activityForContextMenu ? (
              <ActivityMenu
                activity={activityForContextMenu}
                deleteActivity={handleDeleteActivity}
                deleteLoadingIds={deleteLoadingIds}
                openDuplicationModal={handleOpenDuplicationModal}
              />
            ) : (
              <div />
            )
          }
          overlayClassName={styles.dropdown}
        >
          <div />
        </Dropdown>
        <BaseCalendar
          dayMaxEvents={5}
          dayMinWidth={200}
          // duration to use when end is not specified. The reason we remove it for monthly view is because
          // FullCalendar handles it incorrectly really adding this duration. And if event had start date at
          // 23:30, then with extra 30min duration it ends on the next day and fullcalendar displays such event
          // as if it takes 2 days.
          defaultTimedEventDuration={
            viewState.viewMode === CalendarViewMode.MONTH ? "00:00:00" : "00:30:00"
          }
          editable={!isReadOnlyMember}
          eventClassNames={eventClassNamesGetter}
          eventContent={ActivityCalendarEvent}
          eventDidMount={handleRenderEvent}
          eventDurationEditable={!isReadOnlyMember}
          eventMinHeight={12}
          eventResizableFromStart={!isReadOnlyMember}
          events={events}
          eventShortHeight={15}
          eventStartEditable={!isReadOnlyMember}
          headerToolbar={false}
          nowIndicator
          onContainerRef={setCalendarElement}
          onDateClick={isReadOnlyMember ? undefined : handleDateClick}
          onDayCellDidMount={scrollTo8}
          onEventClick={handleClick}
          onEventDrop={handleEventDropResize}
          onEventResize={handleEventDropResize}
          onSelect={isReadOnlyMember ? undefined : handleSelect}
          plugins={[scrollGridPlugin]}
          schedulerLicenseKey={"CC-Attribution-NonCommercial-NoDerivatives"}
          scrollTimeReset={false}
          selectable={viewState.viewMode !== CalendarViewMode.MONTH}
          selectMinDistance={1} // to distinguish onDateClick from onSelect: https://fullcalendar.io/docs/selectMinDistance
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          snapDuration="00:15:00"
          // @ts-ignore
          timeFrameStartDate={viewState.filter.startAt.value}
          viewMode={viewState.viewMode}
        />
      </div>
      {nearbyEvent?.activity && nearbyEvent?.suggestion && (
        <NearbyModal onHide={() => setNearbyEvent(undefined)} suggestion={nearbyEvent.suggestion} />
      )}
      {cadenceSuggestion && (
        <ActivityCadenceModal
          onHide={() => setCadenceSuggestion(undefined)}
          suggestion={cadenceSuggestion}
        />
      )}
      {selectedActivityId && (
        <AnnotationEditModal
          activityId={selectedActivityId}
          element={annotationElement}
          onChange={handleChangeActivity}
          onHide={() => selectActivityId(undefined)}
        />
      )}
    </CalendarContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  deleteLoadingIds: getDeleteLoadingIds(state),
  getUserColorClassName: getUserColorClassNameGetter(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  users: getUsersOfEntireOrganization(state),
});

const mapDispatchToProps = {
  deleteActivity: deleteActivity.request,
  onCreateActivity: showGlobalCreateActivityModal,
  onMoveActivity: moveResizeActivity.request,
  openDuplicationModal: openDuplicationModal.request,
  updateActivityInList,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(Calendar));
